import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SearchIcon from "@mui/icons-material/Search";
import DataGridViewport from "../components/DataGridViewport";
import { useUserContext } from "../hooks/UserContext";
import { useProveedores } from "../hooks/ProveedoresContext";
import { ProductoCatalogoItem } from "../services/productoService";
import { CompraReporteItem, fetchGetReporteCompras } from "../services/compraService";
import { isAbortError } from "../services/fetchService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";

const toDateInput = (date: Date) => date.toISOString().slice(0, 10);
const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
const money = (value: number | undefined) => CurrencyFormatter.format(Number(value ?? 0));
const dateText = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("es-AR");
};

const metricSx = {
  p: 2,
  height: "100%",
  borderLeft: "4px solid",
  borderColor: "primary.main",
};

const ReporteCompras = () => {
  const { user } = useUserContext();
  const { proveedores, loading: loadingProveedores } = useProveedores();
  const [proveedor, setProveedor] = useState<ProductoCatalogoItem | null>(null);
  const [fechaDesde, setFechaDesde] = useState(toDateInput(firstDay));
  const [fechaHasta, setFechaHasta] = useState(toDateInput(today));
  const [fechaDesdeAplicada, setFechaDesdeAplicada] = useState(toDateInput(firstDay));
  const [fechaHastaAplicada, setFechaHastaAplicada] = useState(toDateInput(today));
  const [proveedorAplicado, setProveedorAplicado] = useState<ProductoCatalogoItem | null>(null);
  const [items, setItems] = useState<CompraReporteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    totalSubtotal: 0,
    totalIva: 0,
    totalPercepciones: 0,
    totalBonificacion: 0,
    totalFactura: 0,
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchGetReporteCompras(user, paginationModel.page, paginationModel.pageSize, {
      fechaDesde: fechaDesdeAplicada,
      fechaHasta: fechaHastaAplicada,
      idProveedor: proveedorAplicado?.id,
    }, controller.signal)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
        setTotals({
          totalSubtotal: data.totalSubtotal,
          totalIva: data.totalIva,
          totalPercepciones: data.totalPercepciones,
          totalBonificacion: data.totalBonificacion,
          totalFactura: data.totalFactura,
        });
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        setItems([]);
        setTotal(0);
        setError("No se pudo cargar el reporte de compras.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [fechaDesdeAplicada, fechaHastaAplicada, paginationModel.page, paginationModel.pageSize, proveedorAplicado?.id, user]);

  const aplicarFiltros = () => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFechaDesdeAplicada(fechaDesde);
    setFechaHastaAplicada(fechaHasta);
    setProveedorAplicado(proveedor);
  };

  const columns = useMemo<GridColDef[]>(() => [
    { field: "fecha", headerName: "Fecha", minWidth: 115, valueFormatter: ({ value }) => dateText(value) },
    { field: "proveedor", headerName: "Proveedor", minWidth: 220, flex: 1 },
    { field: "tipo", headerName: "Tipo", minWidth: 135 },
    { field: "comprobante", headerName: "Comprobante", minWidth: 170, valueGetter: (params: any) => `${params.row.letraFactura} ${params.row.puntoVenta}-${params.row.numeroFactura}`.trim() },
    { field: "numeroRemito", headerName: "Remito", minWidth: 120 },
    { field: "subtotal", headerName: "Subtotal", minWidth: 125, align: "right", headerAlign: "right", valueFormatter: ({ value }) => money(value) },
    { field: "iva", headerName: "IVA", minWidth: 115, align: "right", headerAlign: "right", valueGetter: (params: any) => Number(params.row.iva105 ?? 0) + Number(params.row.iva21 ?? 0) + Number(params.row.iva27 ?? 0), valueFormatter: ({ value }) => money(value) },
    { field: "percepciones", headerName: "Perc./Imp.", minWidth: 130, align: "right", headerAlign: "right", valueGetter: (params: any) => Number(params.row.percepcionIva ?? 0) + Number(params.row.ingresosBrutos ?? 0) + Number(params.row.impuestosInternos ?? 0) + Number(params.row.tem ?? 0), valueFormatter: ({ value }) => money(value) },
    { field: "bonificacion", headerName: "Bonif.", minWidth: 115, align: "right", headerAlign: "right", valueFormatter: ({ value }) => money(value) },
    { field: "totalFactura", headerName: "Total", minWidth: 135, align: "right", headerAlign: "right", valueFormatter: ({ value }) => money(value) },
    { field: "formaPago", headerName: "Forma pago", minWidth: 150 },
  ], []);

  const rows = useMemo(() => items.map((item) => ({ ...item, id: item.idCompra })), [items]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <Typography variant="h4">Reporte de compras</Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={proveedores}
              value={proveedor}
              loading={loadingProveedores}
              getOptionLabel={(option) => option.nombre}
              onChange={(_, value) => setProveedor(value)}
              renderInput={(params) => <TextField {...params} label="Proveedor" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="Desde" value={fechaDesde} onChange={(event) => setFechaDesde(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField type="date" label="Hasta" value={fechaHasta} onChange={(event) => setFechaHasta(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" startIcon={loading ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />} onClick={aplicarFiltros} disabled={loading} fullWidth>
              Generar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Subtotal</Typography><Typography variant="h6">{money(totals.totalSubtotal)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">IVA</Typography><Typography variant="h6">{money(totals.totalIva)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Perc./Imp.</Typography><Typography variant="h6">{money(totals.totalPercepciones)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={metricSx}><Typography color="text.secondary">Bonificacion</Typography><Typography variant="h6">{money(totals.totalBonificacion)}</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={2.4}><Paper variant="outlined" sx={{ ...metricSx, borderColor: "success.main" }}><Typography color="text.secondary">Total</Typography><Typography variant="h6">{money(totals.totalFactura)}</Typography></Paper></Grid>
      </Grid>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGridViewport height={650} minWidth={1500}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            disableRowSelectionOnClick
          />
        </DataGridViewport>
      )}
    </Box>
  );
};

export default ReporteCompras;
